import { HttpClient, HttpContext, HttpErrorResponse } from '@angular/common/http';
import { inject, Injectable } from '@angular/core';
import { AiImageModels, GenerateAiImageBodyDto } from '@nusign/picflow-backend-types';
import { BehaviorSubject, catchError, filter, map, of, ReplaySubject, switchMap, throwError } from 'rxjs';
import { OutOfCreditsDialogComponent } from '../account/dialogs/out-of-credits-dialog/out-of-credits-dialog.component';
import { IGNORE_PAYMENT_REQUIRED_ERRORS } from '../network-interceptor';
import { PICFLOW_CONFIG } from '../picflow-config';
import { DialogService } from './dialog.service';
import { SocketService } from './socket.service';

interface AiSocketEvent {
  id: 'fd32c0ae-e4a1-4fc0-905e-ffdbe900d6d8';
  name: 'ai-image.updated';
  payload: {
    payload: {
      error: null;
      id: 'nxr8p2rp1drm60cjaxks3sg1em';
      logs: string;
      model: string;
      output: string | string[];
      status: 'succeeded';
    };
  };
}

@Injectable({
  providedIn: 'root',
})
export class AiService {
  private http = inject(HttpClient);
  private config = inject(PICFLOW_CONFIG);
  private socketService = inject(SocketService);
  private dialog = inject(DialogService);

  showAiUi$ = new ReplaySubject<{ section: string }>(1);
  onNewImages$ = new BehaviorSubject<{ sectionId: string; urls: string[]; params: GenerateAiImageBodyDto }>(undefined);

  public openAiUi(sectionId: string) {
    this.showAiUi$.next({ section: sectionId });
  }

  public generate(params: GenerateAiImageBodyDto, sectionId: string) {
    return this.http
      .post<{
        id: string;
      }>(`${this.config.apiEndpoint}/ai/images/generations`, params, {
        context: new HttpContext().set(IGNORE_PAYMENT_REQUIRED_ERRORS, true),
      })
      .pipe(
        switchMap(resp => {
          this.socketService.sendMessage('ai.join', { id: resp.id }).subscribe();
          return this.socketService.onSocketEvent.pipe(
            filter((event: AiSocketEvent) => event.name === 'ai-image.updated' && event.payload.payload.id === resp.id),
            map((event: AiSocketEvent) => {
              if (event.payload.payload.status === 'succeeded') {
                const output = event.payload.payload.output as string[] | string;
                const urls = Array.isArray(output) ? output : [output];
                this.onNewImages$.next({ sectionId, urls, params });
                return {
                  type: 'success',
                  sectionId,
                  output: urls,
                };
              } else if (event.payload.payload.status === 'processing') {
                return {
                  type: 'progress',
                  sectionId,
                  progress: 0,
                };
              } else {
                console.warn('Unknown status', event.payload.payload);
              }
            })
          );
        }),
        catchError(error => {
          if (!(error instanceof HttpErrorResponse)) return throwError(() => error);
          if (error.error.code !== 'credits_limit_reached') return throwError(() => error);
          this.dialog.open(OutOfCreditsDialogComponent);
          return of({ type: 'error' });
        })
      );

    // .subscribe(resp => {
    //   this.socketService.sendMessage('ai.join', { id: resp.id }).subscribe();
    //   this.socketService.onSocketEvent
    //     .pipe(
    //       takeUntil(this.destroyed$),
    //       filter(event => event.name === 'ai-image.updated')
    //     )
    //     .subscribe((event: AiSocketEvent) => {
    //       if (event.payload.payload.status === 'succeeded') {
    //         const files = (event.payload.payload.output as string[]).map((url, index) => {
    //           return <FromUrlFile>{
    //             name: `${resp.id}_${index}.${this.form.value.output_format}`,
    //             url,
    //             size: 100_000, // 100kb might be a good guess
    //             source: 'url',
    //           };
    //         });
    //         this.close(files);
    //       }
    //     });
  }

  public getFileName(model: AiImageModels, partNumber?: number) {
    const prefix = (() => {
      if (model === 'black-forest-labs/flux-schnell') {
        return `Picflow AI-Lite`;
      }
      return `Picflow AI-Ultra`;
    })();

    const date = new Date();

    // 2024-10-16 at 06.27.48
    const year = date.getFullYear();
    const month = date.getMonth();
    const day = date.getDate();
    const hours = date.getHours();
    const minutes = date.getMinutes();
    const seconds = date.getSeconds();

    const formattedDate = `${year}-${month}-${day} at ${hours}.${minutes}.${seconds}`;

    return `${prefix} ${formattedDate}${partNumber !== undefined ? ` ${partNumber + 1}` : ''}.webp`;
  }
}
